import { SerializedConstructor } from '@langchain/core/load/serializable';
import { TemplateFormat } from '@langchain/core/prompts';
import { FormLabel, Typography } from '@mui/joy';

import { useEffect, useState } from 'react';

import { HubCard } from '@/Pages/HubRepo/HubCard';
import { normalizeKwargsName } from '@/Pages/Playground/utils/Playground.utils';
import { CopyButton, CopyIconButton } from '@/components/CopyButton/CopyButton';
import PromptTemplateFormatDropdown from '@/components/PromptTemplateFormatDropdown';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { getInputVarsFromTemplate } from '@/components/RichTextEditor/utils/RichTextEditor.utils';
import ChainIcon from '@/icons/ChainIcon.svg?react';
import { cn } from '@/utils/tailwind';

import { Manifest, ManifestPreview } from '../../Manifest';
import { ManifestEditorProps, ManifestHubProps } from '../../Manifest.utils';

export function ManifestPromptTemplate(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;

  // first load parse input vars becasue they're not always all there
  useEffect(() => {
    let inputVars = kwargs.input_variables;
    try {
      inputVars = getInputVarsFromTemplate(
        kwargs.template,
        kwargs.template_format
      );
      props.onChange({
        ...props.value,
        kwargs: { ...kwargs, input_variables: inputVars },
      });
    } catch {
      // do nothing
    }
  }, []);

  const name = 'PromptTemplate';

  const [activeKey, setActiveKey] = useState<string | null>(null);

  if (activeKey != null) {
    return (
      <Manifest
        {...props}
        navigation={{
          onBack: () => setActiveKey(null),
          parent: name,
          name: normalizeKwargsName(activeKey),
        }}
        value={kwargs[activeKey]}
        onChange={(value) => {
          const kwargs = (props.value as SerializedConstructor)?.kwargs;
          props.onChange({
            ...props.value,
            kwargs: { ...(kwargs ?? {}), [activeKey]: value },
          });
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className={cn(
          'group rounded-lg border border-secondary bg-background px-4 py-3 transition-colors focus-within:border-brand hover:border-primary hover:focus-within:border-brand',
          props.readOnly && 'border-none p-0'
        )}
      >
        <div className="flex items-start justify-between">
          {props.readOnly ? (
            <div className="mb-2 text-lg font-semibold text-ls-black">
              PromptTemplate
            </div>
          ) : (
            <Typography
              sx={(theme) => ({
                color: theme.vars.palette.neutral[500],
                fontWeight: 600,
                textTransform: 'uppercase',
                minHeight: 32,
                fontSize: 'xs',
                marginTop: '4px',
                marginBottom: '-8px',
              })}
            >
              Template
            </Typography>
          )}

          <div className="flex gap-2 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100">
            <CopyButton
              variant="icon"
              copy={(kwargs.template as string) ?? ''}
              className="user-select-none"
            />
          </div>
        </div>

        <RichTextEditor
          smallText
          variant={'template'}
          initialValue={kwargs.template as string}
          readOnly={props.readOnly}
          templateFormat={kwargs.template_format}
          onChange={(content) => {
            let inputVars = kwargs.input_variables;
            try {
              inputVars = getInputVarsFromTemplate(
                content as string,
                kwargs.template_format
              );
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  template: content,
                  input_variables: inputVars,
                },
              });
            } catch {
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  template: content,
                },
              });
            }
          }}
        />
      </div>
      {!props.hideTemplateFormatSelector && (
        <PromptTemplateFormatDropdown
          templateFormat={kwargs.template_format}
          onTemplateFormatSwitch={(templateFormat: TemplateFormat) => {
            let inputVars = kwargs.input_variables;
            try {
              inputVars = getInputVarsFromTemplate(
                kwargs.template,
                templateFormat
              );
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  template_format: templateFormat,
                  input_variables: inputVars,
                },
              });
            } catch {
              props.onChange({
                ...props.value,
                kwargs: {
                  ...kwargs,
                  template_format: templateFormat,
                },
              });
            }
          }}
        />
      )}
      {props.withAdditionalKeys &&
        Object.keys(kwargs)
          .filter((key) => key !== 'template')
          .map((key) => {
            return (
              <div key={key} className="flex flex-col gap-2">
                <FormLabel>{normalizeKwargsName(key)}</FormLabel>
                <ManifestPreview
                  value={kwargs[key]}
                  onClick={() => setActiveKey(key)}
                  options={props.options}
                />
              </div>
            );
          })}
    </div>
  );
}

export function ManifestPromptTemplateHub(
  props: ManifestHubProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs;
  const templateFormat = kwargs.template_format as TemplateFormat;

  return (
    <HubCard
      {...props.cardProps}
      actions={
        <>
          <div className="flex gap-2 transition-all">
            <CopyIconButton
              copy={(kwargs.template as string) ?? ''}
              className={'p-1.5'}
            />
          </div>
          {props.cardProps.actions}
        </>
      }
      title="PromptTemplate"
      icon={<ChainIcon />}
    >
      <RichTextEditor
        variant={'template'}
        initialValue={kwargs.template as string}
        templateFormat={templateFormat}
        readOnly
      />
    </HubCard>
  );
}
